import { useMemo, useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { newsFeed, trendTags } from '@/mocks/home';

const scopes = [
  { id: 'all', label: '全部快讯' },
  { id: 'today', label: '今日更新' },
];

export default function NewsFlash() {
  const [scope, setScope] = useState('all');

  const list = useMemo(
    () => (scope === 'today' ? newsFeed.filter((item) => item.fresh) : newsFeed),
    [scope],
  );

  const topTags = trendTags.slice(0, 6);
  const maxCount = Math.max(...topTags.map((tag) => tag.count));

  return (
    <section id="flash" className="w-full bg-background-100 border-y border-background-200 py-14 md:py-20 px-4 md:px-8">
      <div className="w-full max-w-[1180px] mx-auto">
        <SectionHeading
          eyebrow="Live Feed"
          icon="ri-time-line"
          title="快讯时间线：最新更新按时间倒序"
          desc="全站板块的新增与跟进都会在这里汇总成一条时间线。看之前先看时间线，事件的来龙去脉一目了然。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-record-circle-line text-primary-500 animate-pulse-soft"></i>
                </span>
                <span className="text-xs md:text-sm text-foreground-600">
                  正在跟进 · 共 {list.length} 条
                </span>
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full border border-background-300 bg-background-50">
                {scopes.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setScope(item.id)}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      scope === item.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <ul className="relative ml-1.5 md:ml-2 border-l border-background-300">
              {list.map((item) => (
                <li key={item.id} className="relative pl-6 md:pl-8">
                  <span
                    className={`absolute -left-[7px] top-7 w-3.5 h-3.5 rounded-full border-2 border-background-100 ${
                      item.fresh ? 'bg-primary-500' : 'bg-background-400'
                    }`}
                  ></span>
                  <div className="pb-7">
                    <div className="flex items-center gap-2 flex-wrap text-xs text-foreground-500 mb-2">
                      <span className="font-heading text-sm text-foreground-800">{item.date}</span>
                      <span className="w-1 h-1 rounded-full bg-background-400"></span>
                      <span>{item.time}</span>
                      {item.fresh ? (
                        <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 text-[11px]">
                          最新
                        </span>
                      ) : null}
                    </div>

                    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 transition-colors hover:border-primary-300">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-[11px] md:text-xs">
                        <i className="ri-bookmark-3-line"></i>
                        {item.board}
                      </span>
                      <h3 className="mt-3 font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">
                        {item.summary}
                      </p>
                      <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between gap-3 flex-wrap">
                        <span className="text-xs text-foreground-500">
                          <i className="ri-fire-line text-primary-500 mr-1"></i>
                          热度 {item.heat}
                        </span>
                        <span className="text-xs text-foreground-500">
                          <i className="ri-chat-3-line text-accent-600 mr-1"></i>
                          {item.comments} 条讨论
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                今日高频标签
              </h3>
              <p className="mt-1.5 text-xs text-foreground-500">按当日检索量排序，每小时刷新一次</p>
              <div className="mt-5 flex flex-col gap-4">
                {topTags.map((tag) => (
                  <div key={tag.id} className="flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs md:text-sm text-foreground-700 whitespace-nowrap">{tag.name}</span>
                      <span className="text-xs text-foreground-500">{tag.count}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500"
                        style={{ width: `${Math.round((tag.count / maxCount) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <span className="inline-flex items-center gap-1.5 text-xs text-accent-800">
                <i className="ri-notification-3-line"></i>
                更新提示
              </span>
              <p className="mt-3 text-xs md:text-sm text-foreground-700 leading-relaxed">
                板块索引每日多次刷新，热门事件板块在晚间时段更新最密集。想跟进某条线，先记住它所在的板块，再按时间线回看。
              </p>
              <button
                type="button"
                className="mt-4 w-full h-10 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看今日全部更新
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}